import axios, { AxiosResponse } from "axios"
import ratmService from "./RATMService"
import ratmHash from "./RATMHash"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"
import scrollTo from "./ScrollTo"
import highlighter from "./Highlighter"
import saveTemplateSetup from "./SaveTemplateSetup"
import RoleManagementAPIResponseTypes from "../types/portalRoleTypes/RoleManagementAPIResponseTypes"
import PortalRoleAndAssignmentTypes from "../types/portalRoleTypes/PortalRoleAndAssignmentTypes"
import TemplateManagementAPIResponseTypes from "../types/portalTemplateTypes/TemplateManagementAPIResponseTypes"
import PortalRoleTemplateWithLnksTypes from "../types/portalTemplateTypes/PortalRoleTemplateWithLnksTypes"
import removeDuplicateTargets from "./RemoveDuplicateTargets"

const { state, manageState } = ratmService
const { buildRoleHashTable, buildTemplateHashTable, purgeHashTable } = ratmHash

const ratmCrud = {
    loggedInUserId: -1,

    getRolesAndtemplates: async (): Promise<void> => {
        try {
            const [rolesAndAssignmentsResponse, roleTemplatesResponse]: [AxiosResponse<RoleManagementAPIResponseTypes>, AxiosResponse<TemplateManagementAPIResponseTypes>] = await Promise.all([
                axios.get<RoleManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/getPortalRolesAndAssignments`, AxiosConfig.configValue("min")),
                axios.get<TemplateManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/roleTemplates`, AxiosConfig.configValue("min"))
            ])

            purgeHashTable(0)
            purgeHashTable(1)
            purgeHashTable(2)

            const rolesAndAssignmentsData = rolesAndAssignmentsResponse.data
            const roleTemplatesData = roleTemplatesResponse.data

            let stateUpdates: { key: string, value: any }[] = [
                { key: "firstLoad", value: false },
                { key: "busyId", value: -1 }
            ]

            if (rolesAndAssignmentsData.Error.Success) {
                stateUpdates.push({ key: "rolesAndAssignments", value: rolesAndAssignmentsData.Data })
                ratmService.rolesAndAssignmentsCopy = rolesAndAssignmentsData.Data
                buildRoleHashTable(rolesAndAssignmentsData.Data)
            } else {
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }

            if (roleTemplatesData.Error.Success) {
                stateUpdates.push({ key: "roleTemplates", value: roleTemplatesData.Data })
                ratmService.roleTemplatesCopy = JSON.parse(JSON.stringify(roleTemplatesData.Data))
                buildTemplateHashTable(roleTemplatesData.Data)
            } else {
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }

            manageState(stateUpdates)
        } catch (error) {
            manageState([{ key: "firstLoad", value: false }])
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    addPortalRolesAndAssignments: async (newRAA: PortalRoleAndAssignmentTypes): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 1 }])

            const res = await axios.post<RoleManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/addPortalRolesAndAssignments`, [newRAA], AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                let raas = state.rolesAndAssignments
                const newRAA = response.Data[0]

                raas.push(newRAA)

                manageState([
                    { key: "rolesAndAssignments", value: raas },
                    { key: "addOrEditRoleId", value: -1 }
                ])

                scrollTo(`ae62298e68c74dbbb19a7539608afb58-${newRAA.Role.PortalRoleId}`, 500, { behavior: "smooth", block: "end", inline: "nearest" })
                highlighter([`ae62298e68c74dbbb19a7539608afb58-${newRAA.Role.PortalRoleId}`], "#28a74550")

                await ratmCrud.getRolesAndtemplates()
            } else {
                manageState([{ key: "busyId", value: -1 }])
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }
        } catch (error) {
            manageState([{ key: "busyId", value: -1 }])
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    editPortalRolesAndAssignments: async (editedRAA: PortalRoleAndAssignmentTypes): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 1 }])
            const res = await axios.post<RoleManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/editPortalRolesAndAssignments`, [removeDuplicateTargets(editedRAA)], AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                await ratmCrud.getRolesAndtemplates()
                manageState([
                    { key: "busyId", value: -1 },
                    { key: "addOrEditRoleId", value: -1 }
                ])
            } else {
                await ratmCrud.getRolesAndtemplates()
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }
        } catch (error) {
            await ratmCrud.getRolesAndtemplates()
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    deletePortalRolesAndAssignments: async (PortalRoleId: number): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 3 }])

            const res = await axios.post<RoleManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/deletePortalRolesAndAssignments`, [PortalRoleId], AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                await ratmCrud.getRolesAndtemplates()
                manageState([{ key: "deleteRoleId", value: -1 }])
            } else {
                await ratmCrud.getRolesAndtemplates()
                snackBar({ type: "error", text: `Failed to delete role: ${response.Error.Message}`, timeout: 5000 })
            }
        } catch (error) {
            await ratmCrud.getRolesAndtemplates()
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    //********************************************************Templates************************************************************************************/
    updatePortalTemplates: async (templateWithLinksToUpdate: PortalRoleTemplateWithLnksTypes): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 2 }])

            const res = await axios.post<TemplateManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/updateRoleTemplates`, [saveTemplateSetup(templateWithLinksToUpdate)], AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                await ratmCrud.getRolesAndtemplates()
                manageState([{ key: "addOrEditTemplateId", value: -1 }])
            } else {
                await ratmCrud.getRolesAndtemplates()
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }
        } catch (error) {
            await ratmCrud.getRolesAndtemplates()
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    addPortalTemplates: async (templateWithLinksToAdd: PortalRoleTemplateWithLnksTypes): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 2 }])

            const res = await axios.post<TemplateManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/addRoleTemplates`, [saveTemplateSetup(templateWithLinksToAdd)], AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                await ratmCrud.getRolesAndtemplates()
                manageState([{ key: "addOrEditTemplateId", value: -1 }])
            } else {
                await ratmCrud.getRolesAndtemplates()
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }
        } catch (error) {
            await ratmCrud.getRolesAndtemplates()
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    },

    deletePortalTemplates: async (templateIdsToDelete: number[]): Promise<void> => {
        try {
            manageState([{ key: "busyId", value: 4 }])

            const res = await axios.post<TemplateManagementAPIResponseTypes>(`${process.env.REACT_APP_API_URL}/api/user/deleteRoleTemplates`, templateIdsToDelete, AxiosConfig.configValue("min"))
            const response = res.data

            if (response.Error.Success) {
                await ratmCrud.getRolesAndtemplates()
                manageState([{ key: "deleteTemplateId", value: -1 }])
            } else {
                await ratmCrud.getRolesAndtemplates()
                snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
            }
        } catch (error) {
            await ratmCrud.getRolesAndtemplates()
            snackBar({ type: "error", text: "An error occurred", timeout: 5000 })
        }
    }
}

export default ratmCrud